/**
 * OCR misread sanity pass — Build 6 / v1.4.6.
 *
 * Bug this fixes: the Build 5 device log showed phantom single-digit ids
 * inside otherwise-clean ascending runs, e.g.
 *
 *     [17, 1, 19, 20, 21, 22, 23, 24]    ← "1" should be 18
 *     [72, 74, 75, 7, 77, 78, 79, 80]    ← "7" should be 76
 *
 * The Pokémon GO Pokédex grid reads strictly ascending by National Dex id as
 * the scanner walks cells left-to-right / top-to-bottom, so a value that is an
 * order of magnitude below BOTH of its in-frame neighbours is almost always a
 * dropped-trailing-digit OCR error (OCR kept the leading "1" of "18", the "7"
 * of "76"). Build 5's relaxed instant-apply gate (confidence 55 / signature
 * 0.5) let these single-digit misreads through.
 *
 * This module is a PURE, side-effect-free pass over one frame's already-
 * resolved observations (in grid/slot order). It does NOT touch the slot
 * mapping (`pogoSlotMapping`) — the slot index is per-frame 1..16 and cannot
 * anchor an absolute id once the trainer scrolls. Neighbours in the SAME frame
 * are the reliable anchor.
 *
 * Behaviour:
 *   - Tight-bracket rule (Nexus Dex hardening): when BOTH in-frame neighbours
 *     are present and exactly two apart (…127, [x], 129…), the cell between them
 *     is uniquely prev+1, so ANY disagreeing read — single-digit OR multi-digit,
 *     including the device log's "11" wedged in the #125-#140 row from a noisy
 *     "11- 0128" OCR — is corrected to that id (or rejected if out of range).
 *   - A single-digit value that breaks the ascending run and is implausibly
 *     small relative to its neighbours is REPAIRED when the misread digit is a
 *     clear prefix of the value the run implies (e.g. run …,17,[1],19,… and 18
 *     starts with 1 → repair 1→18). The repaired id must be strictly between its
 *     neighbours.
 *   - When no confident repair exists, the cell is REJECTED (dropped) rather
 *     than written as a phantom.
 *   - Everything else passes through untouched, so the capture rate for real
 *     low-id catches (a genuine #1 Bulbasaur at the START of a run, not wedged
 *     between #17 and #19) is preserved, and a sparse curated roster whose
 *     neighbours are far apart (G-Max 3,6,9,25,52,99,…) is never touched.
 */

export interface SanityObservation {
  /** National Dex id as currently resolved (post slot-map). */
  dexNumber: number;
  /**
   * Grid column this cell sits in (0-based), when the live adapter produced it.
   * Used ONLY by the appended-digit ladder guard, which compares siblings within
   * the same column. Optional — when absent (upload path / older callers) the
   * ladder guard is a no-op and behaviour is unchanged.
   */
  column?: number;
  /**
   * Sprite/foreground match confidence 0..1 for this cell, when available. The
   * ladder guard only treats two reads as siblings when their sprite confidences
   * are near-identical (a digit-ladder misread of the SAME cell column shares one
   * sprite); independent real cells have independent confidences. Optional.
   */
  spriteConfidence?: number;
  /**
   * 1-based row-major slot this cell occupies in the frame's grid, when the
   * caller knows it. Used ONLY by the slot-ladder consensus guard, which needs
   * the cell's POSITION to know which id the grid can possibly be showing there.
   * Optional — absent ⇒ that guard skips the cell.
   */
  slotIndex?: number;
}

export interface SanityResult<T extends SanityObservation> {
  /** Observations that survived (some with a repaired `dexNumber`). */
  kept: T[];
  /** Count of observations whose `dexNumber` was repaired in place. */
  repaired: number;
  /** Count of observations dropped as unrecoverable misreads. */
  rejected: number;
  /**
   * Count of observations dropped specifically by the appended-digit ladder
   * guard (Root Cause C) — the `7`→`70`→`77` class of OCR sibling. Surfaced for
   * the `ocr sanity {... ladderRejected:n}` telemetry. These are also counted in
   * `rejected`.
   */
  ladderRejected: number;
  /**
   * Count of observations dropped by the slot-ladder consensus guard — cells
   * whose id cannot be at that grid position given what the rest of the frame
   * reads (the mid-scroll smear class). Also counted in `rejected`.
   */
  slotLadderRejected: number;
  /**
   * Count of observations dropped because their dex id appeared more than once
   * in the frame — the `[12,1,1,1,18,19]` class that applied a phantom #001.
   * Also counted in `rejected`. See rejectDuplicateDexIds.
   */
  duplicateRejected: number;
  /**
   * Indices INTO `kept` of the observations whose `dexNumber` was repaired in
   * place. Lets the caller veto only the cells the sanity pass actually touched
   * (Build 6 narrowed frame-veto) rather than demoting every present cell.
   *
   * A repair that resolves to the value already read is NOT a repair and never
   * appears here — see the no-op note in [[sanitizeFrameObservations]].
   */
  repairedIndices: number[];
}

/**
 * Roster/variant context for the sanity pass.
 *
 * `validLowIds` is the set of dex ids that are LEGITIMATE low members of the
 * page's curated roster — for the G-Max Kanto tab these are the real single-
 * digit Gigantamax species #003 (Venusaur), #006 (Charizard), #009 (Blastoise).
 * Those ids are genuine catches that happen to be single-digit, NOT dropped-
 * trailing-digit misreads, so the dropped-digit repair/reject must NOT touch
 * them even when they sit wedged below larger in-frame neighbours (e.g. a G-Max
 * frame reading [3,6,9,25,52,99,...] where 3/6/9 are below 25/52/99).
 *
 * Membership is the discriminator the task requires: a valid roster id (#3/#6/#9)
 * passes; a NON-member single digit on the same page (e.g. an OCR'd #31 that is
 * not a Kanto G-Max member, which would arrive as a wedged low value) is still
 * subject to the existing repair/reject path. Undefined ⇒ no roster context
 * (non-G-Max dexes / upload path) ⇒ the original behaviour is unchanged.
 */
export interface SanityOptions {
  /**
   * True when the page's grid is DENSE — consecutive national-dex ids in
   * reading order, one per slot (the pokemon / shiny / lucky tabs). Only then
   * does `dexNumber - slotIndex` hold a single value across the whole frame,
   * which is what the slot-ladder consensus guard tests. A CURATED sparse tab
   * (mega/gmax/shadow/purified) has arbitrary id gaps between adjacent slots,
   * so the guard is a no-op there. Undefined ⇒ treated as sparse (no guard),
   * which is the pre-existing behaviour for every caller that does not know.
   *
   * NOTE the two ladder guards read this field with OPPOSITE defaults, and that
   * is deliberate. The SLOT-ladder guard needs positional consensus
   * (`dexNumber - slotIndex`), which is meaningless unless the grid is known
   * dense, so silence means "off". The APPENDED-DIGIT ladder is a pure
   * value+confidence heuristic whose only false-positive mode is a curated
   * sparse roster, so silence means "on" and a caller must explicitly declare
   * `denseGrid: false` to disarm it. Each guard defaults to the safe side of its
   * own failure mode.
   *
   * Gating on this replaced gating on `validLowIds == null`, which wrongly
   * disabled the appended-digit guard on the main "pokemon" dex (the caller
   * populates validLowIds there with the real 1-9).
   */
  denseGrid?: boolean;
  /** Dex ids that are legitimate (curated-roster) low members for this page. */
  validLowIds?: ReadonlySet<number>;
}

/** A single-digit id (1-9) is the misread class we guard against. */
function isSingleDigit(n: number): boolean {
  return n >= 1 && n <= 9;
}

/**
 * Decide whether `value` is an implausible single-digit read given its
 * ascending in-frame neighbours, and if so return the repaired id (or null to
 * reject). `prev`/`next` are the neighbouring resolved ids in slot order, or
 * null at a run boundary.
 *
 * Repair rule: a dropped-trailing-digit misread keeps the LEADING digit, so
 * the true id shares `value` as its first digit and slots between the
 * neighbours. We look for the unique id strictly between `prev` and `next`
 * whose first digit equals `value`. If exactly one exists, repair to it;
 * otherwise reject.
 */
export function repairSingleDigit(
  value: number,
  prev: number | null,
  next: number | null,
  options: SanityOptions = {},
): { action: "keep" } | { action: "repair"; to: number } | { action: "reject" } {
  // Variant/roster-aware exception applies to BOTH the tight-bracket rule and
  // the single-digit guard: a legitimate curated-roster low id (#003/#006/#009
  // on the G-Max Kanto tab) is a real catch, never a misread, so leave it alone
  // regardless of its in-frame neighbours.
  if (options.validLowIds?.has(value)) return { action: "keep" };

  // Tight-bracket rule (Nexus Dex live-scan hardening). The PoGo grid reads
  // strictly ascending by National Dex id, so when BOTH neighbours are present
  // and exactly two apart (e.g. …127, [x], 129…), the cell between them is
  // uniquely determined to be prev+1 (128). A read that disagrees — whether a
  // single-digit dropped-trailing-digit misread OR a multi-digit overlay/list
  // token like the device log's "11" wedged in the #125-#140 row — is OCR noise
  // against strong row context. Correct it to the bracketed id (recovering the
  // real catch), or reject when prev+1 is out of the valid dex range. This fires
  // ONLY on the tightest possible bracket, so a sparse curated roster (G-Max's
  // 3,6,9,25,52,99,…) whose neighbours are far apart is never touched.
  if (prev != null && next != null && next - prev === 2) {
    const bracketed = prev + 1;
    if (bracketed === value) return { action: "keep" };
    if (bracketed >= 1 && bracketed <= 1100) {
      return { action: "repair", to: bracketed };
    }
    return { action: "reject" };
  }

  if (!isSingleDigit(value)) return { action: "keep" };

  // A leading single digit at the very start of a run (no larger prev) is a
  // legitimate low id (#1 Bulbasaur opening a Kanto scan). Only values WEDGED
  // below a larger neighbour on at least one side are suspect.
  const prevLarger = prev != null && prev > value + 1;
  const nextLarger = next != null && next > value + 1;
  if (!prevLarger && !nextLarger) return { action: "keep" };

  // Bound the plausible window from whichever neighbours we have.
  const lo = prev != null ? prev + 1 : 1;
  const hi = next != null ? next - 1 : Number.POSITIVE_INFINITY;
  if (lo > hi) return { action: "reject" };

  // Candidate ids in (prev, next) whose first digit equals the misread digit.
  const candidates: number[] = [];
  for (let id = lo; id <= Math.min(hi, lo + 99); id += 1) {
    if (firstDigit(id) === value) candidates.push(id);
  }
  if (candidates.length === 1) return { action: "repair", to: candidates[0] };
  return { action: "reject" };
}

function firstDigit(n: number): number {
  let x = Math.abs(Math.trunc(n));
  while (x >= 10) x = Math.floor(x / 10);
  return x;
}

/**
 * Two reads count as "near-identical sprite confidence" when their confidences
 * are within this band. A digit-ladder misread (e.g. one column OCR'ing `7`,
 * `70`, `77`) reads the SAME sprite three ways, so the confidences cluster
 * tightly (the device log showed 0.770 / 0.777 / 0.794). Independent real cells
 * carry independent sprite confidences and will not cluster this tightly.
 */
const LADDER_CONFIDENCE_EPSILON = 0.05;

/**
 * Root Cause C — appended-digit ladder guard.
 *
 * Device log (Purified Kanto scan): a single grid column produced dexIds
 * [7, 70, 77] at near-identical sprite confidence (0.770 / 0.777 / 0.794),
 * writing a phantom #070. These are not three catches — they are one cell read
 * three ways, the OCR appending a spurious trailing digit to a base reading
 * (`7` → `70`, `7` → `77`).
 *
 * General rule (NEVER a literal-value blocklist — #070 Weepinbell is a real dex
 * member): within a single column, a read C is an appended-digit sibling of a
 * shorter base read B when `C === B * 10 + d` for some digit d ∈ 0..9 AND their
 * sprite confidences are near-identical. The longer (appended) sibling C is the
 * misread and is rejected; the most-supported base B survives. Applied
 * transitively, [7,70,77] keeps the base `7` and drops both `70` and `77`.
 *
 * Safety: in a real PoGo grid, ids within one column differ by the grid stride
 * (≥4), so a genuine column never simultaneously holds `7` and `70`; combined
 * with the near-identical-confidence requirement this fires only on true
 * ladders. No-op when observations carry no `column` / `spriteConfidence`.
 */
export function rejectAppendedDigitLadders<T extends SanityObservation>(
  observations: T[],
): { kept: T[]; ladderRejected: number } {
  // Reject index set — an observation is a ladder sibling of some base in the
  // same column whose confidence is near-identical.
  const rejectedIdx = new Set<number>();

  for (let i = 0; i < observations.length; i += 1) {
    const c = observations[i];
    if (c.column == null || c.spriteConfidence == null) continue;
    const cValue = c.dexNumber;
    if (cValue < 10) continue; // a single digit cannot be an *appended* sibling

    for (let j = 0; j < observations.length; j += 1) {
      if (i === j) continue;
      const b = observations[j];
      if (b.column == null || b.spriteConfidence == null) continue;
      if (b.column !== c.column) continue;
      // Is C the base B with one extra trailing digit?  C === B*10 + d, d∈0..9.
      const d = cValue - b.dexNumber * 10;
      if (d < 0 || d > 9) continue;
      if (Math.abs((c.spriteConfidence ?? 0) - (b.spriteConfidence ?? 0)) > LADDER_CONFIDENCE_EPSILON) {
        continue;
      }
      rejectedIdx.add(i);
      break;
    }
  }

  if (rejectedIdx.size === 0) {
    return { kept: observations, ladderRejected: 0 };
  }
  const kept = observations.filter((_, idx) => !rejectedIdx.has(idx));
  return { kept, ladderRejected: rejectedIdx.size };
}

/**
 * Smallest consensus group the slot-ladder guard will trust, and how much
 * bigger than the runner-up it must be. Three cells reading the same offset is
 * already a shape OCR noise does not produce by accident (each junk read lands
 * on its own offset), and the 2× margin means a genuinely ambiguous frame —
 * where two offsets are similarly supported, e.g. the grid moved a row mid-read
 * — is left completely alone instead of being resolved by coin-flip.
 */
const SLOT_LADDER_MIN_CONSENSUS = 3;
const SLOT_LADDER_MARGIN = 2;

/**
 * Slot-ladder consensus guard — the mid-scroll SMEAR class.
 *
 * Device trace 2026-07-20: one frame OCR'd `[13,14,16,10,60,700,7,4]` at slots
 * `[1,2,4,6,7,8,9,12]` while the very next frame read the same cells as
 * `13,14,16,17…24`. The junk reads are the same digits smeared across a row
 * boundary — `19`→`60`, `20`→`700`, `21`→`7`, `18`→`10`. Every existing guard
 * missed them: the single-digit rules only look at immediate neighbours (`60`
 * sits happily between `10` and `700`), the region cross-check only caught
 * `700`, and `#60` was applied to a dex the trainer was not even looking at.
 *
 * The invariant they all break is positional. On a dense grid the page shows
 * consecutive ids in reading order, so EVERY cell satisfies
 * `dexNumber - slotIndex === k` for one frame-wide constant `k` — whatever row
 * the grid is scrolled to. Real reads therefore agree on `k`; a smeared read
 * lands on its own private `k` (12 for the honest cells above; 4, 53, 692, −2,
 * −8 for the five junk ones). We take the modal `k`, require it to be clearly
 * supported, and drop the cells that disagree.
 *
 * This is deliberately positional rather than value-based: it needs no roster,
 * no region, and no knowledge of which ids are plausible, so it catches a smear
 * that happens to land on a perfectly legal dex id — which is exactly what
 * `#60` was. Pure and total; a no-op unless `denseGrid` is set and enough cells
 * carry a `slotIndex`.
 */
export function rejectSlotLadderOutliers<T extends SanityObservation>(
  observations: T[],
  options: SanityOptions = {},
): { kept: T[]; slotLadderRejected: number } {
  if (!options.denseGrid) return { kept: observations, slotLadderRejected: 0 };

  const offsets = new Map<number, number>();
  for (const obs of observations) {
    if (obs.slotIndex == null) continue;
    const k = obs.dexNumber - obs.slotIndex;
    offsets.set(k, (offsets.get(k) ?? 0) + 1);
  }
  if (offsets.size < 2) return { kept: observations, slotLadderRejected: 0 };

  let modalK = 0;
  let modalCount = 0;
  let runnerUpCount = 0;
  for (const [k, count] of offsets) {
    if (count > modalCount) {
      runnerUpCount = modalCount;
      modalK = k;
      modalCount = count;
    } else if (count > runnerUpCount) {
      runnerUpCount = count;
    }
  }
  if (modalCount < SLOT_LADDER_MIN_CONSENSUS) {
    return { kept: observations, slotLadderRejected: 0 };
  }
  if (modalCount < runnerUpCount * SLOT_LADDER_MARGIN) {
    return { kept: observations, slotLadderRejected: 0 };
  }

  // A cell with no slotIndex has no position to check, so it passes untouched.
  const kept = observations.filter(
    (obs) => obs.slotIndex == null || obs.dexNumber - obs.slotIndex === modalK,
  );
  return { kept, slotLadderRejected: observations.length - kept.length };
}

/**
 * Drop every cell of a dex id that appears MORE THAN ONCE in one frame.
 *
 * Device trace 2026-07-23: a frame OCR'd `[12,1,1,1,18,19]` at slots
 * `[9,10,11,12,14,15]` — three adjacent cells all reading `1`. One carried a
 * caught sprite, so #001 was applied to a dex the trainer was nowhere near
 * (they were around #100). #007 landed the same way on a later frame.
 *
 * Every existing guard let it through, and none of them could have caught it:
 *  - the single-digit repair looks only at immediate neighbours, and `1`
 *    wedged between `1` and `1` reads as a perfectly ordered run;
 *  - the appended-digit ladder compares a base against `B*10+d` siblings, and
 *    `1` vs `1` is not that shape;
 *  - the slot-ladder consensus needs SLOT_LADDER_MIN_CONSENSUS (3) cells
 *    agreeing on one offset, and this frame's modal offset had 2. It bailed
 *    out and reported `slotLadderRejected: 0`. The honest cells did not even
 *    agree with each other here (`grid phase` was `ambiguous:true`), so no
 *    amount of tuning that threshold would have rescued this frame.
 *
 * The invariant this guard uses is far simpler and needs no positional
 * reasoning at all: A DENSE PAGE CANNOT SHOW THE SAME DEX NUMBER TWICE. Every
 * cell is a distinct species, so a repeat is proof of a misread — without
 * knowing which id is right, which slot is right, or where the grid is
 * scrolled to.
 *
 * ALL copies are dropped, not all-but-one. We cannot tell which (if any) of the
 * duplicates was the honest read, and the cost is asymmetric: dropping a real
 * cell loses one frame of evidence that the very next frame re-supplies, while
 * keeping a wrong one writes a false catch the user then has to find and undo.
 * That is the same precision-over-recall trade the empty-state floors make.
 *
 * GATED ON DENSITY, because duplicates are LEGAL on a curated dex — verified
 * against device screenshots on 2026-07-23: Mega Kanto shows 0006 twice
 * (Charizard X and Y), plus 0026 and 0150 twice; G-Max Galar shows 0892 twice
 * (Urshifu's two styles). Running this there would delete real cells. Follows
 * the appended-digit ladder's convention of `!== false` — on unless the caller
 * explicitly declares the page sparse, which the live adapter does for
 * mega/gmax/shadow/purified.
 */
export function rejectDuplicateDexIds<T extends SanityObservation>(
  observations: T[],
  options: SanityOptions = {},
): { kept: T[]; duplicateRejected: number } {
  if (options.denseGrid === false) {
    return { kept: observations, duplicateRejected: 0 };
  }
  const counts = new Map<number, number>();
  for (const obs of observations) {
    counts.set(obs.dexNumber, (counts.get(obs.dexNumber) ?? 0) + 1);
  }
  const kept = observations.filter((obs) => (counts.get(obs.dexNumber) ?? 0) < 2);
  return { kept, duplicateRejected: observations.length - kept.length };
}

/**
 * Run the sanity pass over one frame's observations, IN SLOT ORDER. Returns a
 * new array (input is not mutated) plus repair/reject tallies for diagnostics.
 */
export function sanitizeFrameObservations<T extends SanityObservation>(
  observations: T[],
  options: SanityOptions = {},
): SanityResult<T> {
  // Root Cause C runs FIRST: drop appended-digit ladder siblings (7→70→77) so
  // the neighbour-context repair below sees a clean ascending run. Indices into
  // the post-ladder `survivors` array are what the repair pass tracks, keeping
  // `repairedIndices` consistent for the narrowed frame-veto caller.
  //
  // The ladder heuristic assumes a DENSE region grid, where ids within one
  // column differ by the grid stride (≥4) so a column can never legitimately
  // hold both a base B and an appended sibling B*10+d. A CURATED SPARSE roster
  // (G-Max: 3,6,9,25,52,99,…) breaks that assumption — #9 (row 0) and #99
  // (row 1) genuinely share a column, and #99=#9*10+9 would be a false ladder
  // hit. On those pages the roster-membership gate (validateDexMember) is the
  // authoritative guard for spurious ids.
  //
  // 2026-07-21 FIX — gate on `denseGrid`, NOT on `validLowIds == null`.
  // `validLowIds` was standing in for "curated/sparse", and it was the wrong
  // proxy: the caller also populates it for the MAIN "pokemon" dex (hardcoded
  // 1-9, see dexLiveFrameAdapter), which silently disabled this guard on the
  // densest grid there is — one of the very pages the comment claimed still kept
  // it. `denseGrid` is the heuristic's actual precondition, is already computed
  // by the caller from roster curation + G-Max stride, and is what the
  // slot-ladder guard directly below already gates on.
  //
  // Net effect: re-armed on `pokemon` (unchanged for shiny/lucky/perfect/xxs/xxl,
  // which were already dense-and-rosterless); still off for
  // purified/shadow/mega/gmax, whose sparse rosters can legitimately hold both
  // #9 and #99 in one column.
  // `!== false`, not truthiness: a caller that says nothing keeps the guard (its
  // only false-positive mode is a sparse roster, so silence is safe), while a
  // caller that KNOWS the page is sparse disarms it explicitly. See SanityOptions.
  // Duplicates go FIRST. A repeated id poisons every rule below it: the
  // single-digit repair reasons about an ASCENDING run, and `…12, 1, 1, 1, 18…`
  // is not one, so the junk both escapes and corrupts the context its honest
  // neighbours are judged against.
  const { kept: postDuplicates, duplicateRejected } = rejectDuplicateDexIds(
    observations,
    options,
  );

  const { kept: postLadder, ladderRejected } =
    options.denseGrid !== false
      ? rejectAppendedDigitLadders(postDuplicates)
      : { kept: postDuplicates, ladderRejected: 0 };

  // Slot-ladder consensus runs next, so the smeared reads are gone before the
  // neighbour rules below try to reason about an ascending run they are not
  // part of. (On the device frame that applied a phantom #60, the smear also
  // dragged the run out of order, which is how `10` slipped past the
  // single-digit guard: its "neighbours" were themselves junk.)
  const { kept: survivors, slotLadderRejected } = rejectSlotLadderOutliers(postLadder, options);

  const kept: T[] = [];
  const repairedIndices: number[] = [];
  let repaired = 0;
  let rejected = duplicateRejected + ladderRejected + slotLadderRejected;

  for (let i = 0; i < survivors.length; i += 1) {
    const obs = survivors[i];
    const prev = i > 0 ? survivors[i - 1].dexNumber : null;
    const next = i < survivors.length - 1 ? survivors[i + 1].dexNumber : null;
    const decision = repairSingleDigit(obs.dexNumber, prev, next, options);
    if (decision.action === "keep") {
      kept.push(obs);
    } else if (decision.action === "repair" && decision.to === obs.dexNumber) {
      // NO-OP REPAIR (device trace 2026-07-20). The single-digit rule treats a
      // low id as "wedged" whenever its next neighbour is more than one above
      // it — which is what a GAP in the row looks like, not a misread. For the
      // run …4, 5, 7… it resolves `5` to its only candidate, 5, and the old
      // code booked that as a repair. The caller demotes every repaired cell as
      // an unreliable read (dexLiveFrameAdapter's narrowed frame-veto), so #5
      // was blocked from writing on every frame where #6 happened to be
      // unreadable — 25 frames of it in the device log, while #7 and #8 on the
      // same row committed instantly. A read the pass agrees with is a KEEP:
      // nothing was corrected, so there is nothing to distrust.
      kept.push(obs);
    } else if (decision.action === "repair") {
      repairedIndices.push(kept.length);
      kept.push({ ...obs, dexNumber: decision.to });
      repaired += 1;
    } else {
      rejected += 1;
    }
  }

  return {
    kept,
    repaired,
    rejected,
    duplicateRejected,
    ladderRejected,
    slotLadderRejected,
    repairedIndices,
  };
}
